<template>
	<AppLayout :title="trans('menu.association.representative-rotations.register')">
		<template #actions>
			<PrimaryButton v-if="registration.candidate > -1" :color="Color.Green" @click.stop="confirm">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.confirm') }}
			</PrimaryButton>
		</template>
		<div class="px-3 py-2 flex flex-col lg:flex-row items-stretch lg:items-end gap-3">
			<LabeledObject full>
				<template #label>{{ trans('admin.users.rotations.delegation') }}</template>
				<div class="input-like w-full text-gray-400">{{ rotation.college.name }}</div>
			</LabeledObject>
			<LabeledObject full>
				<template #label>{{ trans('admin.users.rotations.election_datetime') }}</template>
				<div class="input-like w-full text-gray-400">{{ dayjs(rotation.election_begins_at).format(trans('general.dayjs.datetime')) }}</div>
			</LabeledObject>
			<LabeledObject full required>
				<template #label>{{ trans('admin.users.rotations.register.candidate') }}</template>
				<select id="select-candidate" v-model.number="registration.candidate">
					<option value="1">{{ trans('general.yes') }}</option>
					<option value="0">{{ trans('general.no') }}</option>
				</select>
			</LabeledObject>
		</div>
		<div v-if="registration.candidate === 1" class="px-3 py-2 flex flex-col gap-3">
			<LabeledObject full required>
				<template #label>{{ trans('admin.users.rotations.register.student_group_participation') }}</template>
				<select id="select-student-group-participation" v-model.number="registration.student_group_participation">
					<option value="1">Yes</option>
					<option value="0">No</option>
				</select>
			</LabeledObject>
			<LabeledObject v-if="registration.student_group_participation" full required>
				<template #label>{{ trans('admin.users.rotations.register.student_group_description') }}</template>
				<textarea v-model="registration.student_group_description"/>
			</LabeledObject>
			<LabeledObject full required>
				<template #label>{{ trans('admin.users.rotations.register.student_council_participation') }}</template>
				<select id="select-student-council-participation" v-model="registration.student_council_participation">
					<option v-for="option in RotationCandidateParticipation" :key="`participation-option-student-council-${option}`" :value="option">{{ trans(`admin.users.rotations.register.participation_options.${option}`) }}</option>
				</select>
			</LabeledObject>
			<LabeledObject v-if="registration.student_council_participation === 'won'" full>
				<template #label>{{ trans('admin.users.rotations.register.student_council_resignation') }}</template>
				<select id="select-student-council-resignation" v-model.number="registration.student_council_resignation">
					<option value="1">Yes</option>
					<option value="0">No</option>
				</select>
			</LabeledObject>
			<LabeledObject full required>
				<template #label>{{ trans('admin.users.rotations.register.counseling_participation') }}</template>
				<select id="select-counseling-participation" v-model="registration.counseling_participation">
					<option v-for="option in RotationCandidateParticipation" :key="`participation-option-counseling-${option}`" :value="option">{{ trans(`admin.users.rotations.register.participation_options.${option}`) }}</option>
				</select>
			</LabeledObject>
			<LabeledObject full required>
				<template #label>{{ trans('admin.users.rotations.register.certificate') }}</template>
				<template #action>{{ trans('admin.users.rotations.register.certificate_description') }}</template>
				<input type="file" accept="application/pdf" @change="loadFile($event)">
			</LabeledObject>
			<LabeledObject full required>
				<template #label>{{ trans('admin.users.rotations.register.reasons') }}</template>
				<textarea v-model="registration.reasons"/>
			</LabeledObject>
			<LabeledObject full>
				<template #label>{{ trans('admin.users.rotations.register.observations') }}</template>
				<textarea v-model="registration.observations"/>
			</LabeledObject>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {router} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import {defineProps, ref, type Ref} from 'vue'
import {showValidationErrors} from '@/helpers'
import AppLayout from '@/Layouts/AppLayout.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {type RotationCandidate} from '@/Types/Periods/Rotations/RotationCandidate'
import {RotationCandidateParticipation} from '@/Types/Enums/RotationCandidateParticipation'

const props = defineProps<{
	rotation:Rotation,
}>()

const rotation:Ref<Rotation> = ref(props.rotation)
const registration:Ref<RotationCandidate> = ref({
	candidate: -1,
	student_group_participation: 0,
	student_group_description: '',
	student_council_participation: '',
	student_council_resignation: 0,
	counseling_participation: '',
	certificate: null,
	reasons: '',
	observations: '',
} as RotationCandidate)
const sending:Ref<boolean> = ref(false)

function loadFile(event):void{
	if(event.target && event.target.files){
		registration.value.certificate = event.target.files[0]
	}
}

function confirm():void{
	if(!sending.value){
		sending.value = true
		const formData = new FormData
		formData.append('candidate', registration.value.candidate)
		formData.append('student_group_participation', registration.value.student_group_participation)
		formData.append('student_group_description', registration.value.student_group_description)
		formData.append('student_council_participation', registration.value.student_council_participation)
		formData.append('student_council_resignation', registration.value.student_council_resignation)
		formData.append('counseling_participation', registration.value.counseling_participation)
		if(registration.value.certificate){
			formData.append('certificate', registration.value.certificate)
		}
		formData.append('reasons', registration.value.reasons)
		formData.append('observations', registration.value.observations)
		axios.post(route('association.representative-rotations.registrations.store', rotation.value.id), formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(() => {
			router.get(route('association.representative-rotations.index'))
		}).catch(showValidationErrors).finally(() => {
			sending.value = false
		})
	}
}
</script>